// ── @matix/types ──────────────────────────────────────────────────────────
//
// Hand-curated domain types shared across the three apps.
// Per the locked stack: API request/response types will eventually be
// auto-generated from pos-api's OpenAPI 3.1 spec into a sibling file
// (`./generated.ts`) and re-exported from here.

// ── Currency ─────────────────────────────────────────────────────────────

/**
 * Display-oriented currency DTO. Sourced from `mstr_Currencies` joined via
 * `mstr_Subsidary_currency` (BaseCurrency=1) on the backend. Receipt
 * templates and screen money formatters render `code` (e.g. "EGP", "SAR",
 * "AED") and use `precision` for `toFixed()`.
 *
 * Localized names are kept for surfaces that want a full name (e.g. an
 * admin currency-picker dropdown) but are not used in money formatting.
 */
export interface Currency {
  code: string;
  name: string | null;
  name_ar: string | null;
  precision: number;
}

/** Fallback used by frontends when the backend hasn't yet returned a currency. */
export const FALLBACK_CURRENCY: Currency = {
  code: 'EGP',
  name: 'Egyptian Pound',
  name_ar: 'جنيه مصري',
  precision: 2,
};

// ── Branding helpers ─────────────────────────────────────────────────────
export type Branded<T, B> = T & { readonly __brand: B };

export type CashierId = Branded<number, 'CashierId'>;
export type BranchId = Branded<number, 'BranchId'>;
export type SubsidiaryId = Branded<number, 'SubsidiaryId'>;
export type CashDrawerId = Branded<number, 'CashDrawerId'>;
export type TerminalId = Branded<number, 'TerminalId'>;
export type ShiftId = Branded<number, 'ShiftId'>;
export type OrderId = Branded<number, 'OrderId'>;
export type ItemId = Branded<number, 'ItemId'>;
export type CategoryId = Branded<number, 'CategoryId'>;
export type CustomerId = Branded<number, 'CustomerId'>;

// ── Auth ─────────────────────────────────────────────────────────────────
export type CashierRole = 'cashier' | 'manager' | 'admin';

/**
 * Authorization-relevant cashier permissions, embedded in the JWT.
 * Mirrored from `pos-api/src/plugins/auth.ts` CashierJwtPermissions.
 */
export interface CashierJwtPermissions {
  can_change_store: boolean;
  can_edit_tax: boolean;
  can_change_price_in_return: boolean;
  max_receipt_discount_perc: number;
  show_cost_price: boolean;
  /**
   * `Security_Users.ShowReceiptItemDiscount` — the per-user "Allow item
   * discount" tick on the admin Users form. Gates the per-LINE discount in the
   * cart; the header discount is `allow_overall_discount` +
   * `max_receipt_discount_perc`, which is a different permission. Optional for
   * back-compat with tokens minted before the field existed.
   */
  allow_item_discount?: boolean;
  /**
   * `Security_Users.ShowReceiptOverallDiscount` — the per-user "Allow invoice
   * discount" tick (the legacy till's allowinvoiceDiscount). false = no
   * discount on the whole invoice at all (coupons still apply). Optional:
   * a token minted before the field existed reads as allowed; the server
   * re-checks the column on every order.
   */
  allow_invoice_discount?: boolean;
  /**
   * `Security_Users.IsCallCenter` — the per-user "Is call center user" checkbox
   * on the admin Users form, which predates the Call Center module.
   *
   * This flag ALONE decides which screen a signed-in user lands on. The POS
   * `Call Center` permission authorizes the agent ENDPOINTS but does not route,
   * because a tenant whose admin group holds the whole permission tree grants
   * it without meaning to and would lose the till for every administrator.
   * See `isCallCenterAgent` in the cashier app. Optional for back-compat with
   * tokens issued before the field existed; absent reads as "not an agent".
   */
  is_call_center?: boolean;
  /**
   * `Security_Users.ChangeSalesPriceMode` — the per-user "Change selling
   * price" dropdown on the admin Users form.
   *
   *   1 = no change    2 = increase only    3 = full change
   *
   * **0 and NULL mean NOT CONFIGURED, and read as ALLOWED** — see
   * `priceChangeRule` in the cashier app for why, and read it through that
   * rather than comparing the number here. Optional for back-compat with
   * tokens minted before the field existed; absent reads as allowed.
   */
  change_sales_price_mode?: number;
  /**
   * POS permission codes the user has - sourced from
   * Security_UserRoles + Security_RolePermissions + SecurityUserPermissions,
   * filtered down to POS-relevant IDs.
   *
   * Read this through useCanI(POS_PERMISSIONS.X) rather than
   * directly - that way adding/removing codes is a one-line change
   * to the catalog instead of a hunt-the-call-site refactor.
   */
  codes?: number[];
  /**
   * POS permission NAMES (Security_lkp_Permissions.PermissionNameEn) the user
   * has under the POS module. This is the authoritative gating key — names are
   * stable across tenant DBs whereas `codes` (PermissionIDs) are not. Read via
   * useCanI(POS_PERMISSIONS.X). `codes` is a derived, canonical convenience.
   */
  permission_names?: string[];
}

export interface CashierJwtPayload {
  sub: number;
  /** What the person types to log in — Security_Users.LoginName. */
  username: string;
  /**
   * The person's REAL name — Security_Users.Name / .Name_AR. Printed on the
   * receipt under "Cashier"; `username` is not who the customer dealt with.
   *
   * Optional on the wire: a token minted before these claims existed carries
   * neither, and consumers fall back to `username`. Nobody is logged out by
   * the change — they see the login name until their next login.
   */
  display_name?: string | null;
  display_name_ar?: string | null;
  tenant_db: string;
  role: CashierRole;
  branch_id: number;
  branch_name?: string;
  subsidiary_id: number;
  cash_drawer_id: number;
  terminal_id: number | null;
  shift_id: number | null;
  /**
   * Authorization-relevant permissions. Optional on the wire because
   * tokens issued before Phase 8 don't carry this field — frontend code
   * should default missing values conservatively (e.g. treat
   * max_receipt_discount_perc as 0, which forces an override on every
   * discount until the cashier re-logs in).
   */
  permissions?: CashierJwtPermissions;
  iat?: number;
  exp?: number;
}

export interface LoginRequest {
  tenant_slug: string;
  username: string;
  password: string;
  terminal_code?: string;
}

export interface CashierProfile {
  id: number;
  username: string;
  full_name: string | null;
  full_name_ar: string | null;
  role: CashierRole;
  branch_id: number;
  branch_name: string | null;
  branch_name_ar: string | null;
  subsidiary_id: number;
  cash_drawer_id: number;
  cash_drawer_name: string | null;
  terminal_id: number | null;
  permissions: {
    can_change_store: boolean;
    can_edit_tax: boolean;
    can_change_price_in_return: boolean;
    max_receipt_discount_perc: number;
    show_cost_price: boolean;
    allow_item_discount?: boolean;
    allow_invoice_discount?: boolean;
  };
}

export interface LoginResponse {
  token: string;
  expires_at: string;
  cashier: CashierProfile;
}

// ── Catalog ──────────────────────────────────────────────────────────────

export interface CatalogItemModifierOption {
  id: number;
  name: string;
  name_ar: string | null;
  price_delta: number;
  is_default: boolean;
  position: number;
  is_active: boolean;
}

export interface CatalogItemModifierGroup {
  id: number;
  name: string;
  name_ar: string | null;
  min_selections: number;
  max_selections: number;
  /**
   * 'quantity' = "choices with quantity": the cashier types a quantity per
   * option, priced per unit, linked item deducted × it. Absent from older
   * API builds — read it as 'choices'.
   */
  modifier_type?: 'choices' | 'quantity';
  options: CatalogItemModifierOption[];
}

export interface CatalogCategory {
  id: number;
  code: string;
  name: string | null;
  name_ar: string | null;
  parent_id: number | null;
  position: number | null;
  /** Legacy Inv_ProductCategories.ImagePath — a path on the old web server. */
  image_path: string | null;
  /**
   * Admin-uploaded square photo from pos_category_images, as a base64 data-URL.
   * Null when unset, and absent entirely on tenants that never ran migration
   * 027 — so read it defensively.
   */
  image_data?: string | null;
  global_barcode: string | null;
  has_children: boolean;
}

export interface CatalogItem {
  id: number;
  code: string;
  name: string | null;
  name_ar: string | null;
  selling_price: number | null;
  selling_unit_id: number | null;
  allow_decimals: boolean;
  tax_code: string | null;
  allow_tax: boolean;
  tax_type_id: number | null;
  image_path: string | null;
  image_name: string | null;
  /** Square menu photo (base64 data-URL) from pos_item_images. Null when unset. */
  image_data: string | null;
  color: string | null;
  size: string | null;
  brand: string | null;
  pos_featured: boolean;
  display_components_in_pos: boolean;
  kitchen_station: string | null;
  kitchen_printer: string | null;
  print_in_kitchen: boolean;
  item_type: number | null;
  /**
   * Inv_Items.RestockOnReturn — when true the cashier gets a per-line
   * "Restock it?" checkbox on a return of this item. Declining writes a
   * Code 61 Destruction voucher. False means always restock, no checkbox.
   */
  restock_on_return: boolean;
  /** Admin-set per-category display order. One entry per category the item is positioned in. */
  category_positions: { category_id: number; position: number }[];
  category_ids: number[];
  units: CatalogItemUnit[];
  /**
   * Extra item-level barcodes (Inv_Items_Barcode, active only). Scanned like a
   * unit barcode but name no unit — the item's default unit rings up. Optional:
   * a catalogue cached before this field existed simply has none.
   */
  barcodes?: string[];
  modifier_groups: CatalogItemModifierGroup[];
}

export interface CatalogItemUnit {
  unit_row_id: number;
  unit_id: number;
  unit_name: string | null;
  unit_name_ar: string | null;
  barcode: string | null;
  sale_price1: number | null;
  sale_price2: number | null;
  sale_price3: number | null;
  sale_price4: number | null;
  conv: number;
  pos_enabled: boolean;
}

/**
 * An "invoice source" — a named price list bound to a branch
 * (Mstr_Pos_InvoiceSource_Setup, ported from the legacy POS).
 *
 * Selecting one re-prices the whole catalog and the open cart from
 * `CatalogItemUnit.sale_price<price_tier>`, and stamps
 * `Inv_Receipt_h.SourceId` on the receipt. Tenants that don't run multiple
 * price lists have no rows, and the till renders no selector at all.
 */
export interface InvoiceSource {
  id: number;
  code: number;
  name: string;
  name_ar: string;
  /** Which `sale_priceN` field on the unit this source prices from. */
  price_tier: 1 | 2 | 3 | 4;
  /** Flat delivery fee applied while this source is active, or null. */
  delivery_fee: number | null;
  sales_account_id: number | null;
  branch_id: number;
}

export interface InvoiceSourceListResponse {
  /** False when the tenant's DB has no Mstr_Pos_InvoiceSource_Setup table. */
  supported: boolean;
  sources: InvoiceSource[];
}

export interface CategoryListResponse {
  version: string;
  categories: CatalogCategory[];
}

export interface ItemListResponse {
  category_id: number;
  version: string;
  items: CatalogItem[];
}

export interface SearchResponse {
  query: string;
  exact_match: boolean;
  results: CatalogItem[];
}

/**
 * Lightweight featured-items response. Returns only the admin-flagged
 * (pos_featured) items for the cashier's subsidiary — a small payload the
 * landing page can render fast, instead of filtering the full catalog sync.
 */
export interface FeaturedListResponse {
  version: string;
  items: CatalogItem[];
}

export interface SyncResponse {
  version: string;
  changed_categories: CatalogCategory[];
  changed_items: CatalogItem[];
  deleted_item_ids: number[];
}

// ── Order ─────────────────────────────────────────────────────────────────

export type OrderType = 'walkin' | 'takeaway' | 'delivery';
export type OrderStatus = 'open' | 'parked' | 'paid' | 'voided' | 'refunded';

export interface OrderLine {
  line_number: number;
  item_id: number;
  item_code: string;
  item_name: string;
  item_name_ar: string;
  quantity: number;
  unit_price: number;
  line_subtotal: number;
  line_discount: number;
  line_tax: number;
  line_total: number;
  kitchen_station: string | null;
  modifiers?: OrderLineModifier[];
  voided_at?: string | null;
  voided_reason?: string | null;
}

export interface OrderLineModifier {
  modifier_id: number;
  name: string;
  name_ar: string;
  price_delta: number;
}

export type PaymentMethod = 'cash' | 'card' | 'wallet' | 'voucher' | 'other';

export interface OrderPayment {
  method: PaymentMethod;
  amount: number;
  terminal_provider?: 'geidea' | 'tap' | null;
  terminal_ref?: string | null;
  card_last4?: string | null;
}

export interface Order {
  id: number;
  order_number: string;
  order_type: OrderType;
  status: OrderStatus;
  customer_id: number | null;
  customer_name?: string;
  customer_phone?: string;
  delivery_zone_id?: number | null;
  delivery_address?: string;
  delivery_fee?: number;
  delivery_eta_mins?: number;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  paid: number;
  change: number;
  lines: OrderLine[];
  payments: OrderPayment[];
  created_at: string;
  closed_at?: string | null;
  shift_id: number;
  cashier_id: number;
  branch_id: number;
  gl_voucher_id?: number | null;
  inv_receipt_id?: number | null;
}

export interface PaymentType {
  id: number;
  code: number;
  name: string;
  name_ar: string;
  is_cash: boolean;
  icon_name: string | null;
}

export interface PaymentTypesResponse {
  payment_types: PaymentType[];
}

export interface CreateOrderRequest {
  idempotency_key: string;
  branch_id: number;
  store_id?: number | null;
  payments: Array<{
    type_code: number;
    amount: number;
    /** KNET terminal attempt that took this slice (KNET_TERMINAL.md). */
    knet_terminal_txn_id?: number;
  }>;
  /**
   * Refund splits for the return leg (Code 53). Present for return-only and
   * exchange requests; omit for a plain sale. Any sale payment method is a
   * valid refund method. Σ must equal the return leg's net.
   */
  refund_payments?: Array<{
    type_code: number;
    amount: number;
    /** KNET terminal REFUND attempt that paid this slice back. */
    knet_terminal_txn_id?: number;
  }>;
  /**
   * Original invoice this return/exchange is against (archive flow). The
   * server links both new receipts to it and caps the returned quantities.
   */
  parent_receipt_id?: number | null;
  cash_tendered?: number;
  customer?: {
    customer_id?: number;
    /**
     * Which table `customer_id` points at. 'cash' (default) = Inv_CashCustomer;
     * 'credit' = GL_Accounts — the receipt then carries the account in
     * Vendor_ID, and an Unpaid single tender makes it a Code 52 credit invoice.
     */
    customer_type?: 'cash' | 'credit';
    customer_phone?: string;
    customer_name?: string;
    customer_name_ar?: string;
    area?: string;
  } | null;
  items: Array<{
    item_id: number;
    qty: number;
    price: number;
    /** Audit only: the unit price before the cashier typed over it (pos_audit_event price_override). */
    price_override_from?: number;
    unit_id?: number;
    conv?: number;
    discount_value?: number;
    discount_percent?: number;
    notes?: string | null;
    /** Membership redemption: prepaid Inv_MembershipDetail this line draws down. */
    membership_detail_id?: number | null;
    /** Optional per-line salesmen → Inv_Receipt_d.SalesManId / SalesMan2Id. */
    salesman_id?: number | null;
    salesman2_id?: number | null;
    /**
     * Restock decision for a RETURN line (qty < 0), from the cart's "Restock
     * it" checkbox. Only meaningful when the item carries
     * `Inv_Items.RestockOnReturn`. `false` makes the server write a Code 61
     * Destruction voucher alongside the return so the goods come straight
     * back out of stock; `true`/omitted is the plain return.
     */
    restock?: boolean;
    total: number;
    modifiers?: Array<{
      option_id: number;
      group_id: number;
      group_name: string;
      name: string;
      name_ar?: string | null;
      price_delta: number;
    }>;
  }>;
  discount_value?: number;
  discount_percent?: number;
  delivery_fee?: number;
  tax_percentage?: number;
  /** Sale-leg totals (positive lines). 0 for a pure return. */
  expected_subtotal: number;
  expected_tax: number;
  expected_total: number;
  /**
   * Returning from an invoice: its invoice-level discount, carried back as the
   * RETURN's whole-receipt discount (the same share of the returned value).
   * Applied to the return leg; the server holds the refund to what was paid.
   */
  return_discount_value?: number;
  /** Return-leg totals (positive magnitudes). Default 0 for a plain sale. */
  expected_return_subtotal?: number;
  expected_return_tax?: number;
  expected_return_total?: number;
  /** Overall net physically settled = sale total − return total (signed). */
  expected_net?: number;
  order_type?: number | null;
  /** Membership buy (order_type === 5): the package validity window. */
  membership?: {
    start_date: string;
    end_date: string;
  } | null;
  /**
   * Loyalty redemption. The client claims POINTS only; the server prices them
   * at the tenant's rate and books the value as an extra header discount.
   * `discount_value` above must therefore carry the cashier's own discount
   * only — never the redemption — while the `expected_*` totals DO include it.
   */
  /** `as_payment`: the points are a "Loyality" payment split, not a discount (see the API schema). */
  loyalty?: { redeem_points: number; as_payment?: boolean } | null;
  /**
   * Gift cards touched by this order.
   *
   * Unlike loyalty (a discount), a gift card is MONEY: redemption rides the
   * ordinary payment splits on the tenant's gift-card payment code, and this
   * block only says WHICH cards those splits came off. The server enforces
   * Σ redeem == Σ gift-card sale splits, Σ refund == Σ gift-card refund splits,
   * and Σ issue == Σ sale-line totals on the gift-card item — so a client can
   * neither claim gift-card tender without naming a card nor drain more than
   * the receipt credited.
   *
   * Omit entirely for every order that does not involve a gift card.
   */
  gift_cards?: GiftCardOrderBlock | null;
  reference_no?: string | null;
  note?: string | null;
  entry_date?: string;
  delivery_date?: string | null;
  pos_terminal_id?: number | null;
  pos_shift_id?: number | null;
  /**
   * Present only on a sale the till took while it had no network and is now
   * posting from its local queue.
   *
   * It makes the server skip the automatic promotion and membership engines
   * for this receipt. An offline basket is charged at menu prices — the till
   * is never trusted with a discount amount — so pricing those engines at
   * write time would produce a net BELOW the money the cashier actually took,
   * and the payment-sum check would refuse the sale outright. See
   * docs/OFFLINE-CASHIER-PLAN.md §3.
   */
  /**
   * HOLD the order: written as a held invoice (Inv_Receipt_h.IsHolded = 1) on
   * the Unpaid tender, paid later on the Held Orders screen. The server
   * replaces `payments` itself.
   */
  hold?: boolean;
  /** LAUNDRY pay-now: paid at drop-off, still tracked on the laundry board (Pickup / Delivery + ready-by). */
  laundry_order?: boolean;
  offline_capture?: {
    /** When the customer actually paid. Its presence is the flag. */
    captured_at: string;
    /** Which till took it, for support. */
    terminal_ref?: string | null;
    /**
     * The number the till printed on the customer's copy at the time of sale
     * (`OFF-<drawer>-<n>`). The server writes it to the synced invoice's
     * Reference_No and Memo so the customer's paper receipt can be matched to
     * the invoice that only exists after the queue drains.
     */
    provisional_no?: string | null;
  } | null;

  // ── Phase 8 audit fields (Phase 8a.2 wires these into Inv_Receipt_h) ──
  /** GL_Coupon_d.CouponCode redeemed on this receipt. */
  coupon_code?: string | null;
  /**
   * A coupon from the coupon ENGINE (migration 038), distinct from the legacy
   * `coupon_code` above which drives the old GL_Coupon_* path. Send the code
   * only — the server re-validates it and prices the discount itself.
   */
  coupon?: { code: string } | null;
  /** mstr_DiscountReasons.ID for manual discounts. */
  discount_reason_id?: number | null;
  /** Security_Users.Id of the manager who approved an over-limit discount. */
  manager_override_user_id?: number | null;
  /** "Change Salesman" — credit the receipt (Empoyee_ID) to this user. Absent = the cashier. */
  employee_id?: number | null;

  /**
   * Phase 9 — delivery payload, required when order_type === 2.
   * When present, delivery.delivery_fees drives NetValue (overriding
   * top-level delivery_fee). Server validates customer/address/driver
   * ownership before opening the write transaction.
   */
  delivery?: OrderDeliveryInput;
}

export interface OrderDeliveryInput {
  customer_id: number;
  /**
   * Optional — a delivery books without one when the customer has no address
   * on file (`Inv_Receipt_h.CashCustomerDetailId` stays NULL). When sent, the
   * server checks it belongs to `customer_id`.
   */
  address_id?: number | null;
  /** Optional — dispatchers commonly assign a driver after the order is taken. */
  driver_id?: number | null;
  /** Flat fee added after VAT. Defaults to 0. */
  delivery_fees: number;
  /** ISO or naive wall-clock datetime. */
  delivery_date: string;
  reference_no?: string | null;
}

export interface OrderDeliveryResponse {
  customer: { id: number; name: string | null; phone: string | null };
  /** Null when the order was taken without an address. */
  address: { id: number; summary: string } | null;
  /** Null when no driver was assigned at creation time. */
  driver: { id: number; name: string | null; phone: string | null } | null;
  delivery_fees: number;
  delivery_date: string;
  reference_no: string | null;
  delivery_status: { id: number; name: string | null } | null;
}

export interface CreateOrderResponse {
  /** True when the order was written as a HELD invoice (request `hold: true`). */
  is_held?: boolean;
  order_id: number;
  entry_no: number;
  entry_date: string;
  totals: {
    subtotal: number;
    discount_value: number;
    delivery_fee: number;
    tax_value: number;
    net_value: number;
  };
  idempotent_replay: boolean;
  /**
   * `Inv_Receipt_h.PrintCount` as it now stands — 1 for a receipt just sold,
   * and one higher for each edit, so the slip the cashier prints on the spot
   * carries the same number a later reprint of it will.
   *
   * Optional: an older server does not send it, and the receipt then prints
   * exactly as it does today (no marker).
   */
  print_count?: number;
  /**
   * Present on the response to an EDIT: who just saved it and when, so the
   * copy printed on the spot carries the same "edited by / on" a later reprint
   * reads from the change log. Absent on a new sale and from an older server.
   */
  last_edit?: { at: string; by_name: string | null; by_name_ar?: string | null } | null;
  /** Echoed back when the request supplied order_type. */
  order_type?: number | null;
  /** Present when order_type === 2 and the receipt was just created. */
  delivery?: OrderDeliveryResponse;

  /**
   * Automatic promotions the server applied to this order, named.
   *
   * Their money is ALREADY inside `totals.discount_value` — this list only
   * explains it. Present only when at least one applied, so a tenant without
   * promotions sees the response shape it has always seen.
   */
  promotions?: Array<{ promotion_id: number; name: string; name_ar?: string | null; discount_amount: number; level?: 'line' | 'order' }>;

  /**
   * Each SALE line's share of the item-level promotions, in the order the sale
   * lines were sent (0 where none) — already inside the totals, printed in the
   * line's discount. Absent when no item-level promotion applied.
   */
  promotion_line_discounts?: number[];

  /** How the request resolved: plain sale, pure return, or exchange. */
  mode?: 'sale' | 'return' | 'exchange';
  /** The sale leg (Code 51). Present for 'sale' and 'exchange'. */
  sale_receipt?: { order_id: number; entry_no: number; net_value: number };
  /** The return leg (Code 53). Present for 'return' and 'exchange'. */
  return_receipt?: { order_id: number; entry_no: number; net_value: number };
  /**
   * The Destruction voucher (Code 61) written for returned lines the cashier
   * chose NOT to restock. Absent unless at least one such line was present.
   * `net_value` is the write-off at average cost, which the tenant's costing
   * job refines afterwards — it can be 0 at creation time.
   */
  destroy_receipt?: { order_id: number; entry_no: number; net_value: number };
  /** Overall net physically settled = saleNet − returnNet (signed). */
  net_settled?: number;
  /**
   * What this receipt did to the customer's loyalty balance. Present only when
   * points actually moved.
   */
  loyalty?: {
    redeemed_points: number;
    redeemed_value: number;
    /**
     * Present when the points were booked as a "Loyality" (Code -2) payment
     * split instead of a discount: that split's amount. The totals then carry
     * the full invoice value.
     */
    paid_as_tender?: number;
    /** Everything credited by this receipt, membership bonus included. */
    earned_points: number;
    /**
     * The slice of `earned_points` granted by membership items
     * (Σ qty × Inv_Items.LoyalityBalance). Absent from older servers.
     */
    membership_bonus_points?: number;
    balance_points: number;
  };
  /**
   * What this receipt did to which gift cards. Present only when cards actually
   * moved.
   *
   * `issued[].card_number` and `issued[].pin` are the ONLY place either value is
   * ever returned — the cashier has to hand them to the customer. Neither is
   * readable again from any later endpoint, so the UI has to show them before it
   * navigates away.
   */
  gift_cards?: GiftCardOrderResult;
}

// ── Gift cards ────────────────────────────────────────────────────────────

export type GiftCardStatus = 'active' | 'inactive' | 'blocked' | 'expired' | 'redeemed';

/** The gift-card block on a create-order request. */
export interface GiftCardOrderBlock {
  /** Cards being spent on the SALE leg. */
  redeem?: Array<{ card_number: string; pin?: string | null; amount: number }>;
  /** Cards being credited on the RETURN leg. Capped at what each paid. */
  refund?: Array<{ card_number: string; amount: number }>;
  /** Cards being SOLD. Must match the sale lines on the gift-card item. */
  issue?: Array<{
    amount: number;
    card_number?: string | null;
    pin?: string | null;
    customer_id?: number | null;
    recipient_name?: string | null;
    recipient_phone?: string | null;
    expires_at?: string | null;
  }>;
}

export interface GiftCardOrderResult {
  redeemed: Array<{ card_number_masked: string; amount: number; balance_after: number }>;
  refunded: Array<{ card_number_masked: string; amount: number; balance_after: number }>;
  issued: Array<{
    card_number: string;
    pin: string | null;
    amount: number;
    expires_at: string | null;
  }>;
}

/** The tenant's gift-card setup, as the cashier app sees it. */
export interface GiftCardConfig {
  enabled: boolean;
  /** False until migration 037 is applied on this tenant. */
  schema_ready: boolean;
  /** Inv_PaymentType.Code the cashier tenders against. Null = no redemption. */
  payment_type_code: number | null;
  /** Inv_Items.ID the "Sell gift card" flow rings on. Null = no selling. */
  sale_item_id: number | null;
  denominations: number[];
  allow_custom_value: boolean;
  min_value: number;
  max_value: number | null;
  require_pin: boolean;
  pin_length: number;
  default_expiry_months: number | null;
  allow_refund_to_card: boolean;
  max_cards_per_order: number | null;
}

/** What a card lookup tells the till. Never more than the card holder knows. */
export interface GiftCardLookup {
  ok: boolean;
  /** A rejection code (GIFT_CARD_BLOCKED, GIFT_CARD_PIN_INVALID, …) when !ok. */
  reason: string | null;
  card_number_masked: string;
  balance: number;
  status: GiftCardStatus;
  expires_at: string | null;
  pin_required: boolean;
}

/** One card that funded a receipt, and how much of it can still come back. */
export interface GiftCardOnReceipt {
  card_number_masked: string;
  card_number: string;
  paid: number;
  already_refunded: number;
  refundable: number;
}

// ── Loyalty ───────────────────────────────────────────────────────────────

/** The tenant's programme, as the cashier app sees it. */
export interface LoyaltyConfig {
  enabled: boolean;
  /** False when loyalty is on but this branch is not part of the programme. */
  branch_eligible: boolean;
  /** Points granted per 1 unit of currency of eligible sales. */
  earn_rate_points: number;
  /** Money value of one point. 0.01 = "100 points is worth 1.00". */
  redeem_rate_value: number;
  min_redeem_points: number;
  max_redeem_percent: number | null;
  points_expiry_months: number | null;
  earn_on_tax: boolean;
  earn_on_delivery: boolean;
  earn_on_discounted: boolean;
}

export type LoyaltyTxnType =
  | 'earn'
  | 'redeem'
  | 'adjust'
  | 'expire'
  | 'refund_reversal'
  | 'cancel_reversal';

/** One immutable row of the points ledger. */
export interface LoyaltyTransaction {
  id: number;
  customer_id: number;
  customer_name: string | null;
  customer_phone: string | null;
  txn_type: LoyaltyTxnType;
  /** Signed: positive credits the customer, negative debits them. */
  points: number;
  money_value: number | null;
  order_id: number | null;
  branch_id: number | null;
  branch_name: string | null;
  reference_id: string | null;
  reversal_of_txn_id: number | null;
  expires_at: string | null;
  reason: string | null;
  notes: string | null;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
}

/** One customer's standing plus their recent statement. */
export interface CustomerLoyalty {
  customer_id: number;
  customer_name: string | null;
  customer_phone: string | null;
  balance_points: number;
  /** What the balance is worth at today's redemption rate. */
  balance_value: number;
  lifetime_earned: number;
  lifetime_redeemed: number;
  /** Points due to expire in the next couple of months. */
  expiring_points: number;
  expiring_at: string | null;
  last_activity_at: string | null;
  history: LoyaltyTransaction[];
}

/** Server-validated pricing for a redemption the cashier is considering. */
export interface LoyaltyQuote {
  ok: boolean;
  reason: string | null;
  balance_points: number;
  points: number;
  value: number;
  remaining_points: number;
  max_redeemable_points: number;
  max_redeemable_value: number;
}

// ── Shift / KDS ───────────────────────────────────────────────────────────

export interface Shift {
  id: number;
  code: string;
  terminal_id: number;
  cashier_id: number;
  opened_at: string;
  closed_at: string | null;
  opening_cash: number;
  opening_denominations?: Record<string, number>;
  expected_cash?: number | null;
  counted_cash?: number | null;
  variance?: number | null;
  blind_close: boolean;
  total_orders?: number;
  total_sales?: number;
  total_refunds?: number;
  total_voids?: number;
}

export type KdsTicketStatus = 'new' | 'in_progress' | 'ready' | 'bumped' | 'recalled';

export interface KdsTicket {
  id: number;
  order_id: number;
  order_number: string;
  station: string;
  status: KdsTicketStatus;
  priority: boolean;
  fired_at: string;
  bumped_at?: string | null;
  lines: Array<{
    item_name: string;
    quantity: number;
    modifiers: string[];
    note: string | null;
  }>;
}

// ── Customers ────────────────────────────────────────────────────────────

export type CustomerType = 'cash' | 'credit';
export type CustomerTier = 'new' | 'bronze' | 'silver' | 'gold' | 'platinum';

export interface CustomerStats {
  visits: number | null;
  total_spent: number | null;
  avg_spend: number | null;
  last_visit: string | null;
  favorite_item: {
    item_id: number;
    name: string | null;
    name_ar: string | null;
    times_ordered: number;
  } | null;
}

export interface CustomerAddress {
  id: number;
  /** GL_Area — الحي (district) on the Saudi National Address layout. */
  area_id: number | null;
  area_name: string | null;
  area_name_ar: string | null;
  /** GL_Governorate — المدينة (city), the parent of area. */
  governorate_id: number | null;
  governorate_name: string | null;
  governorate_name_ar: string | null;
  country_id: number | null;
  /** الرقم الفرعي — Saudi additional number. */
  secondary_number: string | null;
  /** الرمز البريدي — 5-digit postal code. */
  postal_code: string | null;
  /** العنوان المختصر — 4 letters + 4 digits, e.g. RFQB8127. */
  short_address_code: string | null;
  avenue: string | null;
  block: string | null;
  street: string | null;
  /** House number — labelled رقم المبنى (building number) on the KSA layout. */
  house: string | null;
  floor: string | null;
  flat: string | null;
  address_note: string | null;
  latitude: string | null;
  longitude: string | null;
  /**
   * The delivery fee this address's district carries — its zone's
   * `GL_Zones.Delivery_charges`, else `GL_Area.DeliveryFees`. Null (or absent,
   * from an older API) = no fee configured; the till leaves the fee alone.
   */
  delivery_fee?: number | null;
}

export interface AddressInput {
  area_id?: number | null;
  governorate_id?: number | null;
  country_id?: number | null;
  avenue?: string | null;
  block?: string | null;
  street?: string | null;
  house?: string | null;
  floor?: string | null;
  flat?: string | null;
  secondary_number?: string | null;
  postal_code?: string | null;
  short_address_code?: string | null;
  address_note?: string | null;
}

export interface Area {
  id: number;
  name: string | null;
  name_ar: string | null;
  /** GL_Area.GovernorateID — the city this district belongs to. */
  governorate_id?: number | null;
}

/** GL_Governorate option — المدينة on the KSA address layout. */
export interface Governorate {
  id: number;
  name: string | null;
  name_ar: string | null;
  country_id: number | null;
}

/** GL_VendorCategory option for the customer form. */
export interface VendorCategory {
  id: number;
  name: string | null;
  name_ar: string | null;
}

/** mstr_Country option for the address form. */
export interface Country {
  id: number;
  name: string | null;
  name_ar: string | null;
  /** Saudi Arabia — switches the address form to the National Address layout. */
  is_ksa?: boolean;
  /**
   * ISO 3166-1 alpha-2, resolved server-side by name. Restricts the Google
   * address lookup to this country. Absent/null on an unrecognised name.
   */
  region_code?: string | null;
}

// ── Address lookup (Google Maps Platform, via /api/v1/geo) ───────────────

/** A place name in both scripts — either side can be missing. */
export interface Bilingual {
  en: string | null;
  ar: string | null;
}

/**
 * A resolved address. Bilingual on purpose: the tenant's GL_Governorate /
 * GL_Area lookup rows are Arabic, so the English name alone could never be
 * matched against them when auto-filling the city / district combos.
 */
export interface GeoAddress {
  formatted: Bilingual;
  city: Bilingual;
  /** الحي on the Saudi National Address. */
  district: Bilingual;
  region: Bilingual;
  country: Bilingual;
  country_code: string | null;
  street: Bilingual;
  /** رقم المبنى */
  building_number: string | null;
  postal_code: string | null;
  /** الرقم الفرعي */
  secondary_number: string | null;
  /** Echoed back when the query was a Saudi short code. */
  short_address_code: string | null;
  lat: number;
  lng: number;
  place_id: string | null;
  source: 'geocode' | 'places';
  /** Google matched loosely — show the result but ask the cashier to check it. */
  approximate: boolean;
}

export interface GeoConfig {
  enabled: boolean;
  map: boolean;
}

export interface Customer {
  id: number;
  type: CustomerType;
  code: string | null;
  name: string | null;
  name_ar: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  /** GL_VendorCategory FK — cash customers only. */
  category_id: number | null;
  /** Tax registration number — cash customers only. */
  tax_reg_no: string | null;
  /** Birthday as 'YYYY-MM-DD' — cash customers only. */
  date_of_birth: string | null;
  /** 1 = male, 2 = female — cash customers only. */
  gender_id: number | null;
  loyalty_balance: number | null;
  tier: CustomerTier;
  stats: CustomerStats | null;
  /** Delivery addresses — null on search list and for credit customers. */
  addresses: CustomerAddress[] | null;
}

export interface Driver {
  id: number;
  name: string | null;
  name_ar: string | null;
  phone: string | null;
}

export interface CustomerSearchResponse {
  type: CustomerType;
  customers: Customer[];
}

// ── API envelope ─────────────────────────────────────────────────────────
export interface Paginated<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
}

export interface ApiError {
  error: string;
  code: string;
  details?: Record<string, unknown>;
}

export * from './print';

// ── Custom receipt designer (per-tenant) ─────────────────────────────────
export * from './barcode';
export * from './receiptDesign';
export * from './a4Design';

// ── Park & Recall (Phase 6) ──────────────────────────────────────────────
export * from './parked-orders';

// ── Discounts & Coupons (Phase 8) ────────────────────────────────────────
export * from './discounts';

export * from "./permissions";

// -- Receipts management (Phase 5d) --
export * from "./receipts";

// -- Receipt details (Phase 5d.4) --
export * from "./receipts-detail";

// -- Service screen (Phase 7.1) --
export * from "./service";

// Call Center (migration 046) — agent order taking, branch pending list,
// branch + call-centre monitoring, and the admin configuration screen.
export * from "./callCenter";

// Hosted payment gateways (Tap, UPayments, Tabby, Tamara, MyFatoorah) — the
// payment-type codes each one tenders under, mirrored from pos-api.
export * from "./paymentGateways";
export * from "./shipping";

// Business type -> modules (Initial Setup Wizard, migration 065), mirrored
// from pos-api/src/pos/setup/businessProfile.ts.
export * from "./businessProfile";

// KNET card terminal at the till via eSocket.POS (migration 073, KNET_TERMINAL.md).
export * from "./knetTerminal";
export * from './onlineOrders';

// QR menu (view only) — system tags, mirrored from pos-api/src/qr-view/tags.ts.
export * from "./qrView";

// User manual content shared by admin (/manual) and the cashier (pop-up).
export * from "./manual";
export * from "./manualTables";
export * from "./customerMeasurements";
